import * as React from 'react';
import { Survey } from 'survey-react-ui';
import { Model } from 'survey-core';
import { css } from './../../../Webform';
import { fetchQuizForm, updateStepData } from '../../api';
import { Config } from '../../helper';
import { useZustand } from 'use-zustand';
import { pathwayStore } from '../../store';
import BrokenStep from './BrokenStep';
import {
  disableNextButton,
  enableNextButton,
  pageHandler,
  onTextMarkdownHandler,
  highlightElement, buildExplanationPage
} from '../../quiz-helper';
import QuizSummary from "./QuizSummary";

function Preview(props) {
  const {
    clickHandler,
    loaded,
    config: {
      label,
      text,
      cta,
    } = {},
  } = props;

  if (loaded) {
    return null;
  }

  return (
    <div className='page__section page__section--light-gray page__cta-section'>
      <h3 className='h--small lead text-center'>{label}</h3>
      <p className='p--large lead text-center text-color-gray mb-4'>{text}</p>
      <p className='text-center mb-0'>
        <button
          className='nv-cta-primary nv-cta-primary--small'
          onClick={() => {
            clickHandler()
          }}>
          {cta}
        </button>
      </p>
    </div>
  );
}

function QuizForm(props) {
  const {
    survey,
    completeCallback,
    formId,
    submission,
    pathwayID,
    step,
    loaded,
    setHasError,
    setErrorText,
    setDataToSubmit
  } = props;

  if (!loaded) {
    return null;
  }

  function buildPayload(formId, submissionData) {
    return {
      form_id: formId,
      submission_data: submissionData,
    };
  }

  survey.onComplete.add((sender, options) => {
    setDataToSubmit(sender.data);
    updateStepData(pathwayID, step.id, buildPayload(formId, sender.data))
      .then(response => {
        completeCallback();
      })
      .catch(error => {
        console.log(error);
        setErrorText(Config.I18n.Errors.SubmissionFailure)
        setHasError(true);
      });
  });

  // Disable scroll behavior
  survey.scrollElementToTop = function () {
  };
  survey.scrollToQuestion = function () {
  };
  survey.scrollToElement = function () {
  };
  survey.focusFirstQuestionAutomatic = false;
  survey.autoScrollToQuestion = false;

  survey.onAfterRenderQuestion.add(highlightElement);

  return (
    <div className='quiz-form-wrapper'>
      <Survey model={survey} css={css}/>
    </div>
  );
}

export default function Quiz(props) {
  const {step} = props;
  const {config} = step;
  const {formId = null} = config;
  const [loaded, setLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const [survey, setSurvey] = React.useState(null);
  const [submission, setSubmission] = React.useState(null);
  const [dataToSubmit, setDataToSubmit] = React.useState(null);

  const {
    updateStep,
    pathwayID,
    stepUserData,
  } = useZustand(pathwayStore, (state) => ({
    pathwayID: state.pathwayID,
    updateStep: state.updateStep,
    stepUserData: state.stepUserData,
  }));

  let userData = stepUserData(step.id);

  const doSetLoaded = () => {
    setLoaded(true);
  }

  React.useEffect(() => {
    fetchQuizForm(formId)
      .then(([data]) => {
        const {submission} = data;

        const pagesWithExplanation = [];
        let index = 0;
        for (const page of data?.pages || []) {
          const explanationPage = buildExplanationPage({...page}, index);

          pagesWithExplanation.push({...page});
          pagesWithExplanation.push({...explanationPage});

          index += 1;
        }

        data.pages = pagesWithExplanation;

        let model = new Model(data);
        if (submission?.id && submission?.form_id) {
          const {data: submissionData} = submission;
          setSubmission(submission);
          model.data = submissionData;
        }

        model.pageNextText = Config.I18n.Quiz.RevealAnswer;
        model.showPrevButton = false;

        model.onTextMarkdown.add(onTextMarkdownHandler);
        model.onAfterRenderPage.add(disableNextButton);
        model.onValueChanged.add(enableNextButton);
        model.onCurrentPageChanged.add(pageHandler);

        setSurvey(model);
      }).catch(error => {
      setHasError(true);
    });
  }, []);

  React.useEffect(() => {
    if (userData) {
      const {submission_id = null} = userData;
      if (submission_id) {
        setSubmission(submission_id);
      }
    }
  }, [userData]);

  React.useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = NvAssets.CSS.highlightJs;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const completeStep = () => {
    updateStep(pathwayID, step, Config.Step.Status.completed);
    setSubmission(1);
  }

  if (hasError) {
    return <BrokenStep text={errorText}/>
  }

  if (submission && formId) {
    return <QuizSummary submission={submission} formId={formId} userData={userData} dataToSubmit={dataToSubmit}/>;
  }

  return (
    <div className='pathway-step__details'>
      <div className='pathway-step__content mb-0'>
        <section className='quiz-section'>
          <Preview
            loaded={loaded}
            config={config}
            clickHandler={doSetLoaded}
          />
          <QuizForm
            loaded={loaded}
            formId={formId}
            pathwayID={pathwayID}
            step={step}
            completeCallback={completeStep}
            submission={submission}
            setHasError={setHasError}
            setErrorText={setErrorText}
            survey={survey}
            setDataToSubmit={setDataToSubmit}
          />
        </section>
      </div>
    </div>
  );
}
